const SHARE_CLASSES = {
  "facebook-share": "facebook",
  "messenger-share": "messenger",
  "instagram-share": "instagram",
  "linkedin-share": "linkedin",
  "copy-share": "copy",
  "share-button": "native",
};

const FACEBOOK_APP_ID = "1898071654038876";

function metaContent(selector) {
  return document.querySelector(selector)?.getAttribute("content") || "";
}

function shareUrl(element) {
  if (element.dataset.shareUrl) {
    return element.dataset.shareUrl;
  }

  const canonical = document.querySelector("link[rel='canonical']")?.href;
  const source = canonical || window.location.href;
  const url = new URL(source, window.location.origin);

  return `${url.origin}${url.pathname}`;
}

function shareTitle(element) {
  return (
    element.dataset.shareTitle ||
    metaContent("meta[property='og:title']") ||
    document.title
  );
}

function shareText(element) {
  return (
    element.dataset.shareText || metaContent("meta[property='og:description']")
  );
}

function shareImageUrl(element) {
  return element.dataset.shareFile || metaContent("meta[property='og:image']");
}

function findShareElement(target) {
  const selector = Object.keys(SHARE_CLASSES)
    .map((className) => `.${className}`)
    .join(",");

  return target.closest(selector);
}

function shareKind(element) {
  return Object.entries(SHARE_CLASSES).find(([className]) =>
    element.classList.contains(className),
  )?.[1];
}

function isMobileLike() {
  return /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

function openShareWindow(url) {
  const width = 640;
  const height = 640;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const features = [
    "noopener",
    "noreferrer",
    "menubar=no",
    "toolbar=no",
    "status=no",
    "scrollbars=yes",
    "resizable=yes",
    `width=${width}`,
    `height=${height}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
  ].join(",");

  const popup = window.open(url, "_blank", features);

  if (popup) {
    popup.opener = null;
    popup.focus();
  } else {
    window.location.href = url;
  }
}

async function copyUrl(url) {
  if (window.navigator.clipboard?.writeText) {
    await window.navigator.clipboard.writeText(url);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = url;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

async function imageFileFromUrl(url) {
  if (!url) {
    return null;
  }

  const response = await fetch(url, { credentials: "same-origin" });
  const blob = await response.blob();
  const extension = blob.type.split("/")[1] || "jpg";

  return new File([blob], `share.${extension}`, {
    type: blob.type || "image/jpeg",
  });
}

async function nativeShare(kind, data) {
  if (!window.navigator.share) {
    return false;
  }

  const shareData = {
    title: data.title,
    text: data.text,
    url: data.url,
  };

  if (kind === "instagram") {
    try {
      const file = await imageFileFromUrl(data.imageUrl);

      if (file && window.navigator.canShare?.({ files: [file] })) {
        await window.navigator.share({
          files: [file],
          title: data.title,
          text: data.text,
          url: data.url,
        });
        return true;
      }
    } catch (_error) {
      // Fall back to link sharing below. Remote OG images often cannot be fetched due to CORS.
    }
  }

  if (window.navigator.canShare && !window.navigator.canShare(shareData)) {
    return false;
  }

  await window.navigator.share(shareData);
  return true;
}

function platformUrl(kind, data) {
  const encodedUrl = encodeURIComponent(data.url);
  const encodedTitle = encodeURIComponent(data.title);
  const encodedText = encodeURIComponent(data.text || data.url);
  const redirect = encodeURIComponent(data.url);

  switch (kind) {
    case "facebook":
      return `https://www.facebook.com/dialog/share?app_id=${FACEBOOK_APP_ID}&display=popup&href=${encodedUrl}&redirect_uri=${redirect}`;
    case "messenger":
      if (isMobileLike()) {
        return `fb-messenger://share?link=${encodedUrl}&app_id=${FACEBOOK_APP_ID}`;
      }

      return `https://www.facebook.com/dialog/send?app_id=${FACEBOOK_APP_ID}&link=${encodedUrl}&redirect_uri=${redirect}`;
    case "instagram":
      if (isMobileLike()) {
        return "instagram://story-camera";
      }

      return "https://www.instagram.com/";
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedText}`;
    default:
      return data.url;
  }
}

async function handleShareClick(event) {
  const element = findShareElement(event.target);

  if (!element) {
    return;
  }

  const kind = shareKind(element);

  if (!kind) {
    return;
  }

  event.preventDefault();

  const data = {
    url: shareUrl(element),
    title: shareTitle(element),
    text: shareText(element),
    imageUrl: shareImageUrl(element),
  };

  try {
    if (kind === "copy") {
      await copyUrl(data.url);
      element.dispatchEvent(
        new CustomEvent("share:copied", { bubbles: true, detail: data }),
      );
      return;
    }

    if (await nativeShare(kind, data)) {
      return;
    }
  } catch (error) {
    if (error?.name === "AbortError") {
      return;
    }
  }

  if (kind === "native") {
    await copyUrl(data.url);
    element.dispatchEvent(
      new CustomEvent("share:copied", { bubbles: true, detail: data }),
    );
    return;
  }

  if (kind === "instagram") {
    await copyUrl(data.url);
  }

  openShareWindow(platformUrl(kind, data));
}

export function setupShareButtons() {
  document.addEventListener("click", handleShareClick);
}
